import { Button } from '@/components/ui/Button';
import { DateTimePicker } from '@/components/ui/time-picker/DateTimePicker';
import { Device, Media } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { MediaKind } from '@/models/device.models';
import { SiteConfig } from '@/models/site.models';
import { UTC_ZONE } from '@/models/time.models';
import { ensureNItems } from '@/utils/string';
import { useQuery } from '@tanstack/react-query';
import humanizeDuration from 'humanize-duration';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateTime } from 'luxon';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { liveImageQuery, surroundingImagesQuery } from '../graphql/query';

import { ClientMedia } from '@/models/media';
import { humanizeDurationWithPrefix } from '@/utils/ui.function';
import { v4 as uuid } from 'uuid';
humanizeDuration.languages.shortEn = {
  y: () => 'y',
  mo: () => 'mo',
  w: () => 'w',
  d: () => 'd',
  h: () => 'h',
  m: () => 'm',
  s: () => 's',
  ms: () => 'ms',
};

function getClosestDateIndex(dates: string[], point: string | Date) {
  const pointDate = point instanceof Date ? point : new Date(point);
  let closestIndex = -1;
  let minDiff = Infinity;
  dates.forEach((dateStr, index) => {
    if (!dateStr) return;
    const date = new Date(dateStr);
    const diff = Math.abs(pointDate.getTime() - date.getTime());
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = index;
    }
  });
  return closestIndex;
}

type SelectDeviceImageSliderProps = {
  timestamp?: string;
  defaultLive?: boolean;
  mediaKind: MediaKind;
  device: Device;
  hideLiveButton?: boolean;
  id?: string;
  setMediaFetching?: (fetching: boolean) => void;
  setMediaKind?: (kind: MediaKind) => void;
  onImageChange?: (image: Media | null) => void;
  onLiveImageChange?: (image: Media | null) => void;
  onUserSelectedImageChange?: (image: Media | null) => void;
  onViewingTimestampChange?: (timestamp?: string) => void;
};

export type SelectDeviceImageSliderHandle = {
  handleRefreshClick: () => void;
};

const SelectDeviceImageSlider = forwardRef<
  SelectDeviceImageSliderHandle,
  SelectDeviceImageSliderProps
>(function SelectDeviceImageSlider(
  {
    timestamp,
    defaultLive,
    mediaKind,
    device,
    hideLiveButton,
    id,
    setMediaFetching,
    onImageChange,
    setMediaKind,
    onLiveImageChange,
    onUserSelectedImageChange,
    onViewingTimestampChange,
  },
  ref,
) {
  const deviceTimezone = (device.site?.config as SiteConfig)?.Timezone;
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    (device.site?.config as SiteConfig)?.Timezone,
  );
  // The image selected by the user (not necessarily the one being viewed, which could be updated automatically when live)
  const [selectedImage, setSelectedImage] = useState<Media>();
  // The image currently being viewed
  const [viewingImage, setViewingImage] = useState<Media | null>();
  const [selectedMedia, setSelectedMedia] = useState<Media[]>([]);
  const [nextImage, setNextImage] = useState<Media>();
  const [previousImage, setPreviousImage] = useState<Media>();
  const [viewingTimestamp, setViewingTimestamp] = useState<string>();
  const [surroundingQueryEnabled, setEnableSurroundingQuery] =
    useState<boolean>(false);
  const [liveQueryEnabled, setEnableLiveQuery] = useState<boolean>(
    defaultLive ?? false,
  );
  const [pickedDateTime, setPickedDateTime] = useState<DateTime>();
  const findClosestImage = useRef(false);
  const applyMediaKindFallback = useRef(true);
  const [timestampsDiffHumanized, setTimestampsDiffHumanized] =
    useState<string>();
  const queryInstanceId = useRef<string>(id ?? uuid()).current;

  const {
    data: liveImageData,
    refetch: refetchLiveImage,
    isFetching: liveImageFetching,
  } = useQuery({
    queryKey: ['LiveImage', device.id, queryInstanceId],
    queryFn: () =>
      execute({
        query: liveImageQuery,
        variables: {
          deviceId: device.id,
        },
      }),
    select(data) {
      return data as ClientMedia;
    },
    enabled: liveQueryEnabled,
    refetchInterval: 10000,
    structuralSharing: false,
    gcTime: 0,
  });

  const {
    data: surroundingImages,
    isFetching: surroundingImagesFetching,
    refetch: refetchSurroundingImages,
  } = useQuery({
    queryKey: [
      'SurroundingImages',
      viewingTimestamp,
      device.id,
      mediaKind,
      queryInstanceId,
    ],
    queryFn: () =>
      execute({
        query: surroundingImagesQuery,
        variables: {
          beforeFilter: {
            _and: [
              { kind: { _eq: mediaKind } },
              { deviceId: { _eq: device.id } },
              { time: { _lt: viewingTimestamp } },
            ],
          },
          afterFilter: {
            _and: [
              { kind: { _eq: mediaKind } },
              { deviceId: { _eq: device.id } },
              { time: { _gte: viewingTimestamp } },
            ],
          },
        },
      }),
    enabled: !!viewingTimestamp,
    structuralSharing: false,
    refetchInterval: 10000,
    gcTime: 0,
  });

  useEffect(() => {
    if (!liveQueryEnabled || !liveImageData) {
      return;
    }
    let images: Media[] = [];

    switch (mediaKind) {
      case MediaKind.ORIGINAL:
        images = liveImageData.originalMedia ?? [];
        break;
      case MediaKind.PROCESSED:
        images = liveImageData.processedMedia ?? [];
        break;
      case MediaKind.MASKED:
        images = liveImageData.maskedMedia ?? [];
        break;
      default:
        break;
    }
    if (applyMediaKindFallback.current) {
      // When media kind changes, if there is no images for that kind, we fallback to original
      if (images.length === 0 && mediaKind !== MediaKind.ORIGINAL) {
        images = liveImageData.originalMedia;
        setMediaKind?.(MediaKind.ORIGINAL);
      }
      applyMediaKindFallback.current = false;
    }
    setSelectedMedia(images);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaKind, liveImageData]);

  useEffect(() => {
    const loading =
      (liveQueryEnabled && liveImageFetching) ||
      (surroundingQueryEnabled && surroundingImagesFetching);
    setMediaFetching?.(loading);
  }, [
    liveImageFetching,
    surroundingImagesFetching,
    liveQueryEnabled,
    surroundingQueryEnabled,
    setMediaFetching,
  ]);

  useEffect(() => {
    const latestImage = selectedMedia[0];
    // Set the current viewing image
    setViewingImage(latestImage ? latestImage : null);
    // Set the next as undefined as this is the latest image
    setNextImage(undefined);
    setPreviousImage(selectedMedia[1]);
  }, [selectedMedia]);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }
    setViewingImage(selectedImage);
  }, [selectedImage]);

  useEffect(() => {
    if (viewingImage === undefined) {
      return;
    }
    // Notify parent component when viewingImage changes
    if (onImageChange) {
      onImageChange(viewingImage);
    }
    if (liveQueryEnabled) {
      if (onLiveImageChange) {
        onLiveImageChange(viewingImage);
      }
    } else {
      if (onUserSelectedImageChange) {
        onUserSelectedImageChange(viewingImage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingImage]);

  useEffect(() => {
    if (viewingTimestamp === undefined) {
      return;
    }
    setEnableLiveQuery(false);
    setEnableSurroundingQuery(true);
  }, [viewingTimestamp]);

  useEffect(() => {
    if (!viewingTimestamp) {
      return;
    }
    onViewingTimestampChange?.(viewingTimestamp);
  }, [onViewingTimestampChange, viewingTimestamp]);

  useEffect(() => {
    if (viewingTimestamp) {
      return;
    }
    if (!viewingImage?.time) {
      return;
    }
    onViewingTimestampChange?.(viewingImage.time);
  }, [onViewingTimestampChange, viewingImage?.time, viewingTimestamp]);

  useEffect(() => {
    if (selectedImage && pickedDateTime) {
      const selectedImageDateTime = DateTime.fromISO(
        selectedImage.time,
      ).setZone((device.site?.config as SiteConfig)?.Timezone);

      const humanized = humanizeDurationWithPrefix({
        dateTime: selectedImageDateTime,
        dateTimeToCompare: pickedDateTime,
        returnZero: true,
      });
      setTimestampsDiffHumanized(humanized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);

  useEffect(() => {
    if (!timestamp) {
      findClosestImage.current = false;
      setEnableLiveQuery(true);
      return;
    }
    const dateTime = DateTime.fromISO(timestamp ?? '', {
      zone: deviceTimezone,
    });
    setPickedDateTime(dateTime);
    handlePickedDateChange(dateTime);
  }, [timestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mediaKind) {
      return;
    }

    // When media type changes, if surrounding query is enabled, we want to find the closest image to the current viewingTimestamp in the data set.
    if (surroundingQueryEnabled) {
      findClosestImage.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaKind]);

  useEffect(() => {
    if (!surroundingImages) return;
    // The fetched images is a combination of images from before af after/equal the selected time.
    const beforeImages = ensureNItems(
      (surroundingImages.beforeImages as Media[]).slice().reverse(),
      2,
    );
    const afterImages = ensureNItems(
      surroundingImages.afterImages as Media[],
      2,
    );
    const images = [...beforeImages, ...afterImages];
    const hasImage = images.find((image) => !!image);

    if (hasImage) {
      if (viewingTimestamp && findClosestImage.current) {
        // Find the closest image to the current viewingTimestamp
        const dates = images.map((image) => image?.time ?? '');
        const index = getClosestDateIndex(dates, viewingTimestamp);
        setSelectedImage(images[index]);
        setNextImage(images[index + 1]);
        setPreviousImage(images[index - 1]);
        findClosestImage.current = false;
      } else {
        // Otherwise just update the next/previous based on the current selectedImage.
        // The selectedImage must be in the images array.
        const index = images.findIndex((e) => e?.id === selectedImage?.id);
        setNextImage(images[index + 1]);
        setPreviousImage(images[index - 1]);
      }
    } else {
      setViewingImage(null);
      setNextImage(undefined);
      setPreviousImage(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surroundingImages]);

  const handlePickedDateChange = (dateTime?: DateTime) => {
    if (dateTime) {
      // When user picks a date, we want to find the closest image to that date.
      findClosestImage.current = true;
      setViewingTimestamp(dateTime.toISO()!);
    } else {
      handleSetLiveClick();
    }
    setPickedDateTime(dateTime);
  };

  const handleSetLiveClick = () => {
    setViewingTimestamp(undefined);
    setTimestampsDiffHumanized(undefined);
    setPickedDateTime(undefined);
    setEnableSurroundingQuery(false);
    setEnableLiveQuery(true);
  };

  const handlePrevious = () => {
    const image = previousImage;
    setSelectedImage(image);
    setViewingTimestamp(image?.time);
  };

  const handleNext = () => {
    const image = nextImage;
    setSelectedImage(image);
    setViewingTimestamp(image?.time);
  };

  const localTime = viewingImage
    ? DateTime.fromISO(viewingImage?.time, {
        zone: UTC_ZONE,
      }).setZone((device.site?.config as SiteConfig)?.Timezone)
    : '';

  const utcTime = viewingImage
    ? DateTime.fromISO(viewingImage?.time, { zone: UTC_ZONE })
    : '';

  const handleRefreshClick = useCallback(() => {
    if (liveQueryEnabled) {
      void refetchLiveImage();
      return;
    }

    if (surroundingQueryEnabled) {
      void refetchSurroundingImages();
    }
  }, [
    liveQueryEnabled,
    surroundingQueryEnabled,
    refetchLiveImage,
    refetchSurroundingImages,
  ]);

  useImperativeHandle(ref, () => ({ handleRefreshClick }), [
    handleRefreshClick,
  ]);

  return (
    <div className="flex flex-col gap-2">
      {viewingImage && (
        <div className="flex flex-row justify-between">
          <div className="xs:flex-row flex flex-col justify-between">
            <div className="text-xs">
              <div className="flex">
                <div className="w-10">UTC:</div>
                <div className="col-span-4">
                  {utcTime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}
                </div>
              </div>
              <div className="flex">
                <div className="w-10">Site:</div>
                <div>
                  {localTime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}
                  ({deviceTimezone})
                </div>
              </div>
            </div>
            <div className="text-muted-foreground flex items-center justify-center text-sm font-medium">
              <span className="ms-3 font-semibold">
                {timestampsDiffHumanized}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="@container">
        <div className="flex flex-col gap-2 @min-[400px]:flex-row">
          <DateTimePicker
            timezoneSelectionEnabled={true}
            extraTimezone={selectedTimezone}
            defaultDate={pickedDateTime}
            onChange={handlePickedDateChange}
            onTimezoneChange={setSelectedTimezone}
            defaultTimezone={selectedTimezone}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handlePrevious}
              disabled={
                surroundingImagesFetching || previousImage === undefined
              }
            >
              <ChevronLeft />
            </Button>
            {!hideLiveButton && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSetLiveClick}
                disabled={liveQueryEnabled}
              >
                Live
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleNext}
              disabled={surroundingImagesFetching || nextImage === undefined}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SelectDeviceImageSlider;
